import { useCallback, useEffect, useRef, useState } from 'react'
import { Platform } from 'react-native'
import type { ShotClockCommand } from '../api/streaming.api'
import { playShotClockBeep, unlockShotClockAudio } from '../utils/shotClockSound'
import {
    ShotClockAction,
    ShotClockState,
    ShotClockTone,
    ShotPlayer,
    beepFor,
    displaySeconds,
    initialShotClockState,
    remainingAt,
    shotClockReducer,
    toneFor,
} from '../utils/shotClockUtils'

const TICK_MS = 100

export interface ShotClockSnapshot {
    action: ShotClockCommand
    activePlayer: ShotPlayer
    remainingMs: number
    extAvailable: [boolean, boolean]
}

interface UseShotClockOptions {
    /** Tick/beep only while the extended scoreboard is visible. */
    enabled: boolean
    /** Called after every operator command (not on ticks) so the backend/overlay can mirror it. */
    onCommand?: (snapshot: ShotClockSnapshot) => void
}

function commandFor(action: ShotClockAction, prev: ShotClockState, next: ShotClockState): ShotClockCommand | null {
    switch (action.type) {
        case 'toggle':
            if (prev.status === 'idle') return 'START'
            return next.status === 'paused' ? 'PAUSE' : 'RESUME'
        case 'reset': return 'RESET'
        case 'switch': return 'SWITCH'
        case 'extend': return next === prev ? null : 'EXTEND'
        case 'resetGame': return 'GAME_RESET'
        case 'tick': return prev.status === 'running' && next.status === 'expired' ? 'EXPIRED' : null
        default: return null
    }
}

export function useShotClock({ enabled, onCommand }: UseShotClockOptions) {
    const stateRef = useRef<ShotClockState>(initialShotClockState())
    const [state, setState] = useState<ShotClockState>(stateRef.current)
    const [remainingMs, setRemainingMs] = useState<number>(stateRef.current.remainingMs)
    const lastBeepSecondRef = useRef<number | null>(null)
    const onCommandRef = useRef(onCommand)
    onCommandRef.current = onCommand

    const apply = useCallback((action: ShotClockAction) => {
        const prev = stateRef.current
        const next = shotClockReducer(prev, action)
        if (next === prev && action.type !== 'tick') return
        stateRef.current = next
        const now = 'now' in action ? action.now : Date.now()
        const left = remainingAt(next, now)
        if (action.type !== 'tick') {
            // A fresh command: don't beep for the second we are already on.
            lastBeepSecondRef.current = displaySeconds(left)
            unlockShotClockAudio()
        }
        if (next !== prev) setState(next)
        setRemainingMs(left)
        const cmd = commandFor(action, prev, next)
        if (cmd && onCommandRef.current) {
            onCommandRef.current({
                action: cmd,
                activePlayer: next.activePlayer,
                remainingMs: left,
                extAvailable: next.extAvailable,
            })
        }
    }, [])

    useEffect(() => {
        if (!enabled || state.status !== 'running') return
        const id = setInterval(() => {
            const now = Date.now()
            const cur = stateRef.current
            const sec = displaySeconds(remainingAt(cur, now))
            if (cur.status === 'running' && lastBeepSecondRef.current !== sec) {
                lastBeepSecondRef.current = sec
                const kind = beepFor(sec)
                if (kind) playShotClockBeep(kind)
            }
            apply({ type: 'tick', now })
        }, TICK_MS)
        return () => clearInterval(id)
    }, [enabled, state.status, apply])

    const toggle = useCallback(() => apply({ type: 'toggle', now: Date.now() }), [apply])
    const reset = useCallback(() => apply({ type: 'reset', now: Date.now() }), [apply])
    const switchPlayer = useCallback(
        (to?: ShotPlayer) => apply({ type: 'switch', now: Date.now(), to }),
        [apply],
    )
    const extend = useCallback(() => apply({ type: 'extend', now: Date.now() }), [apply])
    const resetGame = useCallback(() => apply({ type: 'resetGame' }), [apply])
    const hydrate = useCallback(
        (activePlayer: ShotPlayer, extAvailable: [boolean, boolean]) => {
            if (stateRef.current.status !== 'idle') return
            apply({ type: 'hydrate', activePlayer, extAvailable })
        },
        [apply],
    )

    const seconds = displaySeconds(remainingMs)
    const tone: ShotClockTone = toneFor(seconds)

    return { state, seconds, remainingMs, tone, toggle, reset, switchPlayer, extend, resetGame, hydrate }
}

const isTypingTarget = (el: EventTarget | null): boolean => {
    const node = el as HTMLElement | null
    if (!node || !node.tagName) return false
    const tag = node.tagName.toUpperCase()
    return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || node.isContentEditable === true
}

/**
 * Desktop keyboard shortcuts (web only). Keys are matched on `KeyboardEvent.key`
 * lower-cased ('p'), with ' ' for Space. Combos with ctrl/alt/meta are ignored.
 */
export function useHotkeys(handlers: Record<string, () => void>, enabled: boolean, onFirstKey?: () => void) {
    const handlersRef = useRef(handlers)
    handlersRef.current = handlers
    const onFirstKeyRef = useRef(onFirstKey)
    onFirstKeyRef.current = onFirstKey
    const seenKeyRef = useRef(false)

    useEffect(() => {
        if (Platform.OS !== 'web' || !enabled || typeof window === 'undefined') return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.repeat || e.ctrlKey || e.altKey || e.metaKey) return
            if (isTypingTarget(e.target)) return
            const key = e.key.length === 1 ? e.key.toLowerCase() : e.key
            const handler = handlersRef.current[key]
            if (!handler) return
            e.preventDefault()
            const active = document.activeElement as HTMLElement | null
            if (active && active.tagName === 'BUTTON' && typeof active.blur === 'function') active.blur()
            if (!seenKeyRef.current) {
                seenKeyRef.current = true
                onFirstKeyRef.current?.()
            }
            handler()
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [enabled])
}
